import { coerceBooleanProperty } from "@angular/cdk/coercion";
import {
	AfterViewInit,
	ChangeDetectionStrategy,
	Component,
	HostBinding,
	Input,
	OnDestroy,
	OnInit,
	ViewEncapsulation,
} from "@angular/core";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";
import { select, Store } from "@ngrx/store";
import { KycAction } from "@obg/mat.ui.common";
import { CommonState } from "@obg/ngx.common";
import { CORE_ROUTE, Jurisdiction, JurisdictionSelector } from "@obg/ngx.core";
import { PlatformService, TelemetryService, WindowRef } from "@obg/ngx.global.evo";
import { RouteSelector } from "@obg/ngx.router";
import { Observable, Subscription } from "rxjs";
import { filter, skipUntil, take, tap } from "rxjs/operators";

import { KycVerificationAction } from "./sdk/kyc-verification.action";
import { KycVerificationState } from "./sdk/kyc-verification.reducer";
import { KycVerificationSelector } from "./sdk/kyc-verification.selector";

@Component({
	selector: "obg-m-kyc-verification-page",
	templateUrl: "./kyc-verification.page.html",
	styleUrls: ["./kyc-verification.page.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
})
export class KycVerificationPage implements OnInit, OnDestroy, AfterViewInit {
	@HostBinding("class.obg-m-kyc-verification") hostClass = true;

	@Input()
	set hideTitle(value: boolean) {
		this._hideTitle = coerceBooleanProperty(value);
	}
	get hideTitle(): boolean {
		return this._hideTitle;
	}
	@Input()
	set useOverlayLoader(value: boolean) {
		this._useOverlayLoader = coerceBooleanProperty(value);
	}
	get useOverlayLoader(): boolean {
		return this._useOverlayLoader;
	}

	_hideTitle = false;
	_useOverlayLoader = false;
	isActive$: Observable<boolean> | undefined;
	safeUrl$: Observable<SafeResourceUrl | undefined> | undefined;
	private currentJurisdiction: Jurisdiction | undefined;
	private currentJurisdiction$$: Subscription | undefined;
	private documentHeight: () => void;

	constructor(
		private store: Store<CommonState & KycVerificationState>,
		private selector: KycVerificationSelector,
		private telemetryService: TelemetryService,
		private platformService: PlatformService,
		public sanitizer: DomSanitizer,
		private window: WindowRef
	) {
		// Function to update the height of the kyc-verification iframe to match the window's inner height
		// Ensures that the iframe always occupies the full available height, adjusting dynamically on window resize
		this.documentHeight = () => {
			const doc = this.window.native.document.documentElement;
			doc.style.setProperty("--doc-height", `${this.window.native.innerHeight}px`);
		};
	}

	ngOnInit() {
		// If running in a browser environment (not a server-side context like SSR), set the initial height and register the resize listener
		if (this.platformService.isBrowser) {
			this.documentHeight();
			this.window.native.addEventListener("resize", this.documentHeight);
		}

		this.currentJurisdiction$$ = this.store
			.pipe(
				select(JurisdictionSelector.get),
				take(1),
				tap((jurisdiction) => {
					this.currentJurisdiction = jurisdiction;
					this.telemetryService.track({
						RegistrationType: this.currentJurisdiction,
						event: KycAction.AccountVerificationOpen,
					});
				})
			)
			.subscribe();

		this.store.dispatch(KycVerificationAction.getKnowYourCustomerUrl());

		this.safeUrl$ = this.store.pipe(
			select(this.selector.getKnowYourCustomerUrl()),
			skipUntil(
				this.store.pipe(
					select(this.selector.isBusy()),
					filter((isBusy) => !isBusy)
				)
			),
			filter((url) => !!url?.length)
		);

		this.isActive$ = this.store.select(RouteSelector.isActive(CORE_ROUTE.verification.root));
	}

	ngAfterViewInit() {
		if (this.platformService.isBrowser) {
			this.window.native.scroll(0, 0);
		}
	}

	ngOnDestroy() {
		this.telemetryService.track({
			RegistrationType: this.currentJurisdiction,
			ItemClicked: "Close",
			event: KycAction.AccountVerificationClick,
		});
		if (this.currentJurisdiction$$) {
			this.currentJurisdiction$$.unsubscribe();
		}
		if (this.platformService.isBrowser) {
			this.window.native.removeEventListener("resize", this.documentHeight);
		}
	}
}
